import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import type { VehicleDetail as Vehicle } from "../types/vtc";
import { withNext } from "../data/auth";
import { getSession } from "../lib/auth";
import { assetUrl } from "../lib/api";
import { SITE_NAME, absoluteUrl, breadcrumbLd, useSeo } from "../lib/seo";
import { getVehicle } from "../lib/marketplaceApi";
import { getFavouriteIds, requestBooking as sendBookingRequest, setFavouriteListing } from "../lib/driverApi";
import { errorText } from "../lib/api";
import { toast } from "../lib/toast";
import { addDaysIso, cn, daysBetween, eur, longDate, num, today } from "../lib/format";
import { MAX_BOOKING_DAYS, MIN_BOOKING_DAYS, dailyRate, rentalPrice } from "../lib/rentalPrice";
import { getDict, label, useT } from "../i18n";

import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import Badge from "../components/ui/Badge";

const FAVOURITES_KEY = "vtc.favourites";

/** Favourites live in this browser only; storage can be unavailable. */
function readFavourites(): string[] {
  try {
    return JSON.parse(localStorage.getItem(FAVOURITES_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function writeFavourites(slugs: string[]) {
  try {
    localStorage.setItem(FAVOURITES_KEY, JSON.stringify(slugs));
  } catch {
    // Not saved — the toggle still works for this visit.
  }
}

/** "10 days · weekly rate ≈ €425": the cheapest mix of day / week / month rates (same as the API). */
function estimate(v: Vehicle, days: number): { label: string; total: number } {
  const { total, basis } = rentalPrice(days, v);
  return { label: getDict().vehicle.estimate(days, basis), total };
}

function Stars({ value }: { value: number }) {
  const full = Math.round(value);
  return (
    <span className="tracking-[1px]" aria-label={`${value}/5`}>
      <span className="text-ink">{"★".repeat(full)}</span>
      <span className="text-[#D4D4D4]">{"★".repeat(5 - full)}</span>
    </span>
  );
}

function SectionTitle({ children }: { children: string }) {
  return <h2 className="m-0 mt-9 mb-3 text-[15px] font-medium">{children}</h2>;
}

export default function VehicleDetail() {
  const t = useT();
  const { slug = "" } = useParams();
  // Published listings of verified companies (GET /listings/:slug)
  const [loaded, setLoaded] = useState<{
    slug: string;
    vehicle: Vehicle | null;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    getVehicle(slug)
      .then((vehicle) => !cancelled && setLoaded({ slug, vehicle }))
      .catch(() => !cancelled && setLoaded({ slug, vehicle: null }));
    return () => {
      cancelled = true;
    };
  }, [slug]);
  const vehicle = loaded?.slug === slug ? loaded.vehicle : undefined;

  // Car + offer (week and month prices) and breadcrumbs; a missing vehicle is kept out of search
  const vehicleName = vehicle ? [vehicle.brand, vehicle.model, vehicle.version].filter(Boolean).join(" ") : "";
  useSeo(
    vehicle === undefined
      ? null
      : !vehicle
        ? { title: t.vehicle.notFoundTitle, noindex: true }
        : {
            title: t.seo.vehicle.title(vehicleName, vehicle.city),
            description: t.seo.vehicle.description(vehicleName, vehicle.city, eur(vehicle.weekly), eur(vehicle.monthly)),
            path: `/vehicles/${vehicle.slug}`,
            image: vehicle.photos?.[0] ? assetUrl(vehicle.photos[0]) : null,
            type: "product",
            jsonLd: [
              {
                "@context": "https://schema.org",
                "@type": "Car",
                name: vehicleName,
                brand: { "@type": "Brand", name: vehicle.brand },
                model: vehicle.model,
                ...(vehicle.year && { vehicleModelDate: String(vehicle.year) }),
                ...(vehicle.seats && { seatingCapacity: vehicle.seats }),
                ...(vehicle.colour && { color: vehicle.colour }),
                fuelType: vehicle.energy,
                vehicleTransmission: vehicle.transmission,
                description: vehicle.description || t.seo.vehicle.description(vehicleName, vehicle.city, eur(vehicle.weekly), eur(vehicle.monthly)),
                url: absoluteUrl(`/vehicles/${vehicle.slug}`),
                ...(vehicle.photos?.length && { image: vehicle.photos.slice(0, 5).map((p) => assetUrl(p)) }),
                offers: {
                  "@type": "Offer",
                  url: absoluteUrl(`/vehicles/${vehicle.slug}`),
                  businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
                  price: vehicle.weekly,
                  priceCurrency: "EUR",
                  priceSpecification: [
                    { "@type": "UnitPriceSpecification", price: vehicle.weekly, priceCurrency: "EUR", unitCode: "WEE", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "WEE" } },
                    { "@type": "UnitPriceSpecification", price: vehicle.monthly, priceCurrency: "EUR", unitCode: "MON", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" } },
                  ],
                  availability: vehicle.availableUnits > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                  areaServed: vehicle.city,
                  seller: {
                    "@type": "AutoRental",
                    name: vehicle.company.name || SITE_NAME,
                    ...(vehicle.company.publicId && { url: absoluteUrl(`/companies/${vehicle.company.publicId}`) }),
                    ...(vehicle.company.rating && (vehicle.company.reviews ?? 0) > 0 && {
                      aggregateRating: { "@type": "AggregateRating", ratingValue: vehicle.company.rating, reviewCount: vehicle.company.reviews, bestRating: 5, worstRating: 1 },
                    }),
                  },
                },
              },
              breadcrumbLd([[t.seo.breadcrumbHome, "/"], [t.seo.breadcrumbVehicles, "/vehicles"], [vehicleName]]),
            ],
          },
  );

  if (vehicle === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header activeHref="/vehicles" />
        <main
          className="mx-auto w-full max-w-300 flex-1 px-4 pt-6 sm:px-6"
          aria-busy="true"
        >
          <div className="aspect-16/10 max-w-155 animate-pulse rounded-lg bg-surface" />
          <div className="mt-5 h-7 w-64 animate-pulse rounded bg-surface" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header activeHref="/vehicles" />
        <main className="flex-1 px-4 py-20 text-center">
          <h1 className="m-0 mb-2 text-2xl font-medium">
            {t.vehicle.notFoundTitle}
          </h1>
          <p className="m-0 mb-5 text-sm text-muted">
            {t.vehicle.notFoundText}
          </p>
          <Link
            to="/vehicles"
            className="text-sm text-brand hover:text-brand-link"
          >
            {t.vehicle.seeAll}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }
  // Keyed so dates and photo reset when moving to a similar vehicle.
  return <VehiclePage key={slug} vehicle={vehicle} />;
}

function VehiclePage({ vehicle: v }: { vehicle: Vehicle }) {
  const t = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const start = params.get("start") ?? today();
  const end = params.get("end") ?? addDaysIso(start, MIN_BOOKING_DAYS); // default: one day
  const days = daysBetween(start, end);

  const [photo, setPhoto] = useState(0);
  const [favourite, setFavourite] = useState(() =>
    readFavourites().includes(v.slug),
  );
  /** Reference of the request just sent ("R-26-1031"). */
  const [requested, setRequested] = useState("");
  const [notice, setNotice] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const isDriver = getSession()?.role === "driver";

  // Logged-in drivers keep favourites on their account; visitors in this browser
  useEffect(() => {
    if (!isDriver) return;
    getFavouriteIds().then(
      (ids) => setFavourite(ids.includes(v.id)),
      () => {
        // Keep the browser's value
      },
    );
  }, [isDriver, v.id]);

  const name = `${v.brand} ${v.model}`;
  // From 1 day (end = the next day) up to one month; no minimum duration any more
  const dateError =
    days < MIN_BOOKING_DAYS
      ? t.vehicle.dateOrder
      : days > MAX_BOOKING_DAYS
        ? t.vehicle.dateMax(MAX_BOOKING_DAYS)
        : "";
  const price = estimate(v, days);
  // Visitors see the public number ("Rental Company #1042"), not the name
  const companyName = v.company.publicId ? t.common.companyLabel(v.company.publicId) : v.company.name;
  const initials = v.company.publicId ? `#${v.company.publicId}` : v.company.name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("");

  const setDates = (s: string, e: string) => {
    setRequested("");
    setNotice("");
    setParams({ start: s, end: e }, { replace: true });
  };

  const requestBooking = async () => {
    const session = getSession();
    if (!session) {
      // "Your dates are saved": signup brings the driver back here.
      return navigate(
        withNext("/signup", `/vehicles/${v.slug}?start=${start}&end=${end}`),
      );
    }
    if (session.role !== "driver") {
      return setNotice(t.vehicle.driverOnly);
    }
    setSending(true);
    setNotice("");
    try {
      const { booking, message } = await sendBookingRequest({ listingId: v.id, start, end, note: note.trim() || undefined });
      setRequested(booking.ref);
      toast.success(message);
    } catch (err) {
      setNotice(errorText(err));
    } finally {
      setSending(false);
    }
  };

  const toggleFavourite = async () => {
    const slugs = readFavourites().filter((s) => s !== v.slug);
    writeFavourites(favourite ? slugs : [...slugs, v.slug]);
    setFavourite(!favourite);
    if (isDriver) {
      try {
        await setFavouriteListing(v.id, !favourite);
      } catch (err) {
        setFavourite(favourite);
        toast.error(err);
      }
    }
  };

  // Real photos when the company uploaded some, else the named placeholder views
  const photos = v.photos?.length ? v.photos : null;
  const photoViews = photos
    ? photos.map((_, i) => `${t.common.photo} ${i + 1}`)
    : t.vehicle.photoViews;
  const spec = t.vehicle.specs;
  const specs: [string, string | number][] = [
    [spec.year, v.year],
    [spec.fuel, label(t.enums.energy, v.energy)],
    [spec.gearbox, label(t.enums.transmission, v.transmission)],
    [spec.seats, v.seats],
    [spec.colour, v.colour],
    [spec.mileage, `${num(v.mileage)} km`],
    [spec.category, v.category ? label(t.enums.category, v.category) : "—"],
    [spec.fleet, v.fleetSize],
  ];

  const tm = t.vehicle.terms;
  const terms: [string, string][] = [
    [tm.daily, eur(dailyRate(v))],
    [tm.weekly, eur(v.weekly)],
    [tm.monthly, eur(v.monthly)],
    [tm.kmIncluded, tm.kmPerWeek(num(v.kmIncluded))],
    [tm.extraKm, eur(v.extraKm)],
    [tm.deposit, tm.depositValue(eur(v.deposit))],
  ];

  const dateInput =
    "w-full border-0 bg-transparent p-0 text-[13px] text-ink outline-none";

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/vehicles" />

      <main className="mx-auto grid w-full max-w-250 flex-1 items-start gap-6 px-4 pt-4 pb-16 sm:px-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
        <div className="min-w-0">
          <Link
            to={`/vehicles?start=${start}&end=${end}`}
            className="text-xs text-muted no-underline hover:text-ink"
          >
            {t.vehicle.allVehicles}
          </Link>

          {/* Gallery: arrows, swipe, ← → keys, and a slideshow when there are several photos */}
          <Gallery
            count={photoViews.length}
            index={photo}
            onChange={setPhoto}
            autoplay={!!photos}
            prevLabel={t.vehicle.prevPhoto}
            nextLabel={t.vehicle.nextPhoto}
          >
            {photos ? (
              photos.map((src, i) => (
                <img
                  key={src}
                  src={assetUrl(src)}
                  alt={`${name} · ${i + 1}`}
                  aria-hidden={i !== photo}
                  className={cn(
                    "absolute inset-0 h-full w-full object-cover transition-opacity duration-500",
                    i === photo ? "opacity-100" : "opacity-0",
                  )}
                />
              ))
            ) : (
              <span className="font-mono text-xs text-faint">
                {t.common.photo} {photo + 1} / {photoViews.length} ·{" "}
                {photoViews[photo]}
              </span>
            )}
          </Gallery>
          <div className="mt-2 grid grid-cols-5 gap-2">
            {photoViews.map((view, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPhoto(i)}
                aria-label={view}
                aria-pressed={photo === i}
                className={cn(
                  "flex aspect-4/3 cursor-pointer items-center justify-center overflow-hidden rounded border-2 bg-surface px-1 text-[10px] text-faint",
                  photos && "p-0",
                  photo === i
                    ? "border-ink"
                    : "border-transparent hover:bg-surface-hover",
                )}
              >
                {photos ? (
                  <img
                    src={assetUrl(photos[i])}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  view
                )}
              </button>
            ))}
          </div>

          {/* Title */}
          <div className="mt-5 flex flex-wrap gap-1.5">
            {v.company.verified && (
              <Badge tone="green" className="gap-1 text-[11px]">
                ✓ {t.common.verifiedCompany}
              </Badge>
            )}
            <Badge tone="green" className="text-[11px]">
              {t.common.availableUnits(v.availableUnits)}
            </Badge>
            {v.urgent && (
              <span className="rounded bg-brand px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-white">
                {t.common.sponsored}
              </span>
            )}
          </div>
          <h1 className="m-0 mt-3 text-[28px] font-medium tracking-[-0.01em]">
            {name}
          </h1>
          <div className="mt-1 text-[13px] text-muted">
            {[v.version, v.city].filter(Boolean).join(" · ")}
          </div>
          {v.description && (
            <p className="mt-4 mb-0 text-sm leading-relaxed text-ink-soft">
              {v.description}
            </p>
          )}

          <SectionTitle>{t.vehicle.specsTitle}</SectionTitle>
          <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-line-strong sm:grid-cols-4">
            {specs.map(([label, value]) => (
              <div
                key={label}
                className="border-b border-r border-line px-3 py-2.5"
              >
                <div className="text-[11px] text-faint">{label}</div>
                <div className="mt-0.5 text-[13px] font-medium">{value}</div>
              </div>
            ))}
            <div className="hidden bg-surface sm:col-span-2 sm:block" />
          </div>

          <SectionTitle>{t.vehicle.termsTitle}</SectionTitle>
          <div className="border-t border-line">
            {terms.map(([label, value]) => (
              <div
                key={label}
                className="flex justify-between gap-4 border-b border-line py-2.5 text-[13px]"
              >
                <span className="text-muted">{label}</span>
                <span className="text-right font-medium">{value}</span>
              </div>
            ))}
          </div>
          <ul className="mt-3.5 mb-0 list-disc pl-4.5 text-[13px] leading-[1.7] text-ink-soft">
            {v.conditions.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              [t.vehicle.insurance, v.insurance],
              [t.vehicle.maintenance, v.maintenance],
            ]
              // Listings from the API may leave these blank
              .filter(([, text]) => text)
              .map(([title, text]) => (
                <div key={title} className="rounded-lg bg-surface p-4">
                  <b className="text-[13px] font-medium">{title}</b>
                  <p className="m-0 mt-1 text-xs leading-relaxed text-muted">
                    {text}
                  </p>
                </div>
              ))}
          </div>

          <SectionTitle>{t.vehicle.docsTitle}</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {v.requiredDocs.map((d) => (
              <span
                key={d}
                className="rounded border border-line-strong px-2.5 py-1.5 text-xs"
              >
                {t.company.docTypes[d]}
              </span>
            ))}
          </div>
          <p className="mt-2.5 mb-0 text-xs text-muted">{t.vehicle.docsHint}</p>

          {/* Company: rating, links, and its latest published reviews */}
          <div className="mt-7 rounded-lg border border-line-strong p-4">
            <div className="flex gap-3.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-[10px] font-medium text-white">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <b className="text-sm font-medium">{v.companyInfo.realName ?? companyName}</b>
                <div className="mt-0.5 text-xs text-muted">
                  {[
                    v.companyInfo.realName ? companyName : "",
                    v.company.verified ? t.common.verifiedCompany : "",
                    v.companyInfo.area ?? "",
                    v.companyInfo.memberSince ? t.vehicle.memberSince(v.companyInfo.memberSince) : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                <div className="mt-1 text-xs">
                  {v.company.rating ? (
                    <a href={v.company.publicId ? `/companies/${v.company.publicId}#reviews` : "#company-reviews"} className="text-ink">
                      <Stars value={v.company.rating} />{" "}
                      <span className="underline">
                        {v.company.rating} · {t.companyPage.reviewsCount(v.company.reviews ?? 0)}
                      </span>
                    </a>
                  ) : (
                    <span className="text-muted">{t.companyPage.noReviews}</span>
                  )}
                </div>
                {v.company.publicId && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to={`/companies/${v.company.publicId}`}
                      className="inline-flex h-8 items-center rounded bg-ink px-3 text-xs font-medium text-white no-underline"
                    >
                      {t.vehicle.viewCompany}
                    </Link>
                    {!!v.companyInfo.listings && (
                      <Link
                        to={`/companies/${v.company.publicId}#vehicles`}
                        className="inline-flex h-8 items-center rounded bg-surface px-3 text-xs font-medium text-ink no-underline hover:bg-surface-hover"
                      >
                        {t.companyPage.viewAll(v.companyInfo.listings)}
                      </Link>
                    )}
                  </div>
                )}
                <div className="mt-2.5 text-xs text-ink-soft">{t.vehicle.contactHint}</div>
              </div>
            </div>

            {!!v.reviews?.length && (
              <div id="company-reviews" className="mt-4 border-t border-line-strong pt-3">
                <div className="mb-1 text-[13px] font-medium">{t.vehicle.reviewsTitle}</div>
                <ul className="m-0 list-none p-0">
                  {v.reviews.map((r) => (
                    <li key={r.id} className="border-b border-line-strong py-3 last:border-b-0">
                      <div className="flex flex-wrap items-center gap-2 text-[13px]">
                        <b className="font-medium">{r.author}</b>
                        <Stars value={r.rating} />
                        <span className="text-xs text-muted">{longDate(r.date.slice(0, 10))}</span>
                      </div>
                      {r.vehicle && <div className="text-[11px] text-muted">{t.companyPage.rented(r.vehicle)}</div>}
                      <p className="m-0 mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-soft">{r.text}</p>
                    </li>
                  ))}
                </ul>
                {v.company.publicId && (v.company.reviews ?? 0) > v.reviews.length && (
                  <Link to={`/companies/${v.company.publicId}#reviews`} className="text-xs text-brand-link">
                    {t.vehicle.allReviews(v.company.reviews ?? 0)}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Booking card */}
        <aside className="mt-8 rounded-lg border border-line-strong p-4 lg:sticky lg:top-18">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span>
              <b className="text-[26px] font-semibold">{eur(dailyRate(v))}</b>
              <span className="text-xs text-muted"> {t.common.perDay}</span>
            </span>
            <span>
              <b className="text-[15px] font-semibold">{eur(v.weekly)}</b>
              <span className="text-xs text-muted"> {t.common.perWeek}</span>
            </span>
            <span>
              <b className="text-[15px] font-semibold">{eur(v.monthly)}</b>
              <span className="text-xs text-muted"> {t.common.perMonth}</span>
            </span>
          </div>
          <div className="mt-1 text-[11px] text-muted">
            {t.vehicle.depositDirect(eur(v.deposit))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <label className="rounded bg-surface px-2.5 py-1.5">
              <span className="block text-[10px] text-muted">
                {t.vehicle.start}
              </span>
              <input
                type="date"
                value={start}
                onChange={(e) =>
                  setDates(
                    e.target.value,
                    e.target.value >= end
                      ? addDaysIso(e.target.value, MIN_BOOKING_DAYS)
                      : end,
                  )
                }
                className={dateInput}
              />
            </label>
            <label className="rounded bg-surface px-2.5 py-1.5">
              <span className="block text-[10px] text-muted">
                {t.vehicle.end}
              </span>
              <input
                type="date"
                value={end}
                min={addDaysIso(start, MIN_BOOKING_DAYS)}
                max={addDaysIso(start, MAX_BOOKING_DAYS)}
                onChange={(e) => setDates(start, e.target.value)}
                className={dateInput}
              />
            </label>
          </div>

          {dateError ? (
            <div className="mt-2.5 rounded bg-tone-red-bg px-3 py-2 text-xs text-tone-red-fg">
              {dateError}
            </div>
          ) : (
            <>
              <div className="mt-2.5 rounded bg-tone-green-bg px-3 py-2 text-xs text-tone-green-fg">
                {t.vehicle.availability(v.availableUnits, v.fleetSize)}
              </div>
              <div className="mt-3 flex justify-between gap-2 text-xs text-ink-soft">
                <span>{price.label}</span>
                <b className="font-semibold text-ink">≈ {eur(price.total)}</b>
              </div>
            </>
          )}

          {requested ? (
            <div
              role="status"
              className="mt-3.5 rounded bg-tone-blue-bg px-3 py-2.5 text-xs leading-normal text-tone-blue-fg"
            >
              <b className="font-medium">
                {t.vehicle.requestSent(companyName)}
              </b>{" "}
              {t.vehicle.notified}{" "}
              <Link to={`/driver/bookings/${requested}`} className="font-medium text-tone-blue-fg underline">
                {requested}
              </Link>
            </div>
          ) : (
            <>
              {isDriver && !dateError && (
                <label className="mt-3 grid gap-1 text-[11px] text-muted">
                  {t.driverPages.request.note}
                  <textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t.driverPages.request.notePlaceholder}
                    className="w-full resize-y rounded border border-transparent bg-surface px-2.5 py-2 text-xs text-ink outline-none focus:border-brand"
                  />
                </label>
              )}
              <button
                type="button"
                disabled={!!dateError || sending}
                onClick={() => void requestBooking()}
                className="mt-3.5 h-10 w-full cursor-pointer rounded border-0 bg-brand text-[13px] font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? t.driverPages.request.sending : t.vehicle.requestBooking}
              </button>
            </>
          )}
          {notice && (
            <p className="mt-2 mb-0 text-xs text-tone-red-fg">{notice}</p>
          )}

          <button
            type="button"
            onClick={() => void toggleFavourite()}
            aria-pressed={favourite}
            className="mt-2 flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded border-0 bg-surface text-xs font-medium text-ink hover:bg-surface-hover"
          >
            <Heart
              size={13}
              className={favourite ? "fill-tone-red-fg text-tone-red-fg" : ""}
            />
            {favourite ? t.vehicle.savedFavourite : t.vehicle.addFavourite}
          </button>

          <p className="mt-3.5 mb-0 text-[11px] leading-normal text-muted">
            {t.vehicle.disclaimer}
          </p>
        </aside>

        {/* Similar vehicles */}
        <section className="min-w-0 lg:col-start-1">
          <h2 className="m-0 mt-6 mb-3.5 text-lg font-medium">
            {t.vehicle.similar}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(v.similar ?? []).map((s) => (
              <Link
                key={s.id}
                to={`/vehicles/${s.slug}?start=${start}&end=${end}`}
                className="overflow-hidden rounded-lg border border-line-strong text-ink no-underline hover:border-[#C9CACB]"
              >
                <div className="relative flex aspect-16/10 items-center justify-center bg-linear-to-b from-surface to-[#E2E3E4]">
                  {s.photo ? (
                    <img
                      src={assetUrl(s.photo)}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <span className="font-mono text-[10px] text-faint">
                      {t.common.photo} · {s.brand} {s.model}
                    </span>
                  )}
                </div>
                <div className="px-3 py-2.5">
                  <b className="text-[13px] font-medium">
                    {s.brand} {s.model}
                  </b>
                  <div className="mt-0.5 text-[11px] text-muted">
                    {s.city} · {eur(s.weekly)}
                    {t.common.perWeek}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

const SLIDE_MS = 5000;

interface GalleryProps {
  count: number;
  index: number;
  onChange: (index: number) => void;
  /** Moves on by itself every 5 s (paused on hover/focus and for reduced motion). */
  autoplay: boolean;
  prevLabel: string;
  nextLabel: string;
  children: ReactNode;
}

/** Main photo with previous/next arrows, swipe, arrow keys and an optional slideshow. */
function Gallery({ count, index, onChange, autoplay, prevLabel, nextLabel, children }: GalleryProps) {
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);
  const several = count > 1;
  const go = (step: number) => onChange((index + step + count) % count);

  // Restarts after each change, so a click gives the full 5 s on that photo
  useEffect(() => {
    if (!autoplay || !several || paused) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setTimeout(() => onChange((index + 1) % count), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [autoplay, several, paused, index, count, onChange]);

  const arrow =
    "absolute top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-white/85 text-ink shadow transition-opacity hover:bg-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100";

  return (
    <div
      className="group relative mt-2.5 flex aspect-16/10 items-center justify-center overflow-hidden rounded-lg bg-linear-to-b from-surface to-[#E2E3E4]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (!several) return;
        if (e.key === "ArrowLeft") go(-1);
        if (e.key === "ArrowRight") go(1);
      }}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
        setPaused(true);
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        setPaused(false);
        if (start === null || !several) return;
        const dx = e.changedTouches[0].clientX - start;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      {children}
      {several && (
        <>
          <button type="button" aria-label={prevLabel} onClick={() => go(-1)} className={cn(arrow, "left-3")}>
            <ChevronLeft size={20} />
          </button>
          <button type="button" aria-label={nextLabel} onClick={() => go(1)} className={cn(arrow, "right-3")}>
            <ChevronRight size={20} />
          </button>
          <span className="absolute right-3 bottom-3 z-10 rounded bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white">
            {index + 1} / {count}
          </span>
        </>
      )}
    </div>
  );
}
